import {
  Modal,
  ModalOverlay,
  ModalContent,
  Flex,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Spinner,
  Box,
  ModalHeader,
  Heading,
} from '@chakra-ui/react';
import { shipmentEvents } from './events';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { shipmentsApi } from '@texas/api/endpoints/shipmentsApi';
import { useEffect, useMemo, useState } from 'react';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { ShipmentItem } from './ShipmentItem';
import { request } from '@texas/utils/helpers/httpHelpers';
import { useTranslation } from 'react-i18next';
import { useActiveSession } from '@texas/hooks/useSession';
import { ErrorDetails } from '../alert/ErrorDetails';
import { SharedDisclosureProps } from '../types';
import { SamplesTable } from '../samples/SamplesTable';
import { VerifyButton } from '../verifyButton/VerifyButton';

interface Props extends SharedDisclosureProps {
  shipmentId: number;
}

export function UnpackShipmentModal(props: Props) {
  const { t } = useTranslation();
  return (
    <Modal
      isOpen={props.isOpen}
      onClose={props.onClose}
      isCentered={true}
      size="4xl"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('shipment.markShipmentReceived')}</ModalHeader>
        <ModalCloseButton />
        <Inner
          shipmentId={props.shipmentId}
          onUnpack={() => {
            shipmentEvents.onShipmentUnpacked.dispatch();
            props.onClose();
          }}
        />
      </ModalContent>
    </Modal>
  );
}

function Inner({
  shipmentId,
  onUnpack,
}: {
  shipmentId: number;
  onUnpack: () => void;
}) {
  const { t } = useTranslation();
  const { currentUser } = useActiveSession();
  const [unpackIds, setUnpackIds] = useState<number[]>([]);

  const { data, refetch, loading, error } = useApiResource(
    shipmentsApi.getShipment,
  );
  const {
    request: receivedRequest,
    loading: receivedLoading,
    error: receivedError,
  } = useApiRequest(shipmentsApi.unpack);

  useEffect(() => {
    refetch(shipmentId);
  }, [refetch, shipmentId]);

  const performReceived = () =>
    request(
      receivedRequest,
      [shipmentId, { sampleIds: unpackIds, lostSampleIds: [] }],
      () => {
        onUnpack();
      },
    );

  useEffect(() => {
    if (!data) return;
    setUnpackIds(data.samplesInShipment.map((s) => s.sampleId));
  }, [currentUser.id, data]);

  const yourSamples = useMemo(() => {
    return (
      data?.samplesInShipment.filter(
        (x) => x.transport.receiverUser?.id === currentUser.id,
      ) ?? []
    );
  }, [data, currentUser]);

  const othersSamples = useMemo(() => {
    return (
      data?.samplesInShipment.filter(
        (x) => x.transport.receiverUser?.id !== currentUser.id,
      ) ?? []
    );
  }, [data, currentUser]);

  return (
    <>
      {receivedError && <ErrorDetails error={receivedError} />}
      {error && <ErrorDetails error={error} />}
      <ModalBody>
        <Flex flexDir="column" alignItems="center">
          {loading && <Spinner />}
          {data && (
            <Box w="full" py={2}>
              <ShipmentItem
                shipment={{
                  ...data,
                  samplesInShipmentCount: data.samplesInShipment.length,
                }}
              />
            </Box>
          )}
          {yourSamples.length > 0 && (
            <>
              <Heading fontSize="xl" py={4}>
                {t('shipment.yourSamples', { count: yourSamples.length })}
              </Heading>
              <SamplesTable
                samples={yourSamples}
                maxH="400px"
                overflowY="auto"
              />
            </>
          )}
          {othersSamples.length > 0 && (
            <>
              <Heading fontSize="xl" py={4}>
                {t('shipment.othersSamples', { count: othersSamples.length })}
              </Heading>
              <SamplesTable
                samples={othersSamples}
                maxH="400px"
                overflowY="auto"
              />
            </>
          )}
        </Flex>
      </ModalBody>
      <ModalFooter justifyContent="center" flexDir="column">
        <VerifyButton
          label={t('shipment.markReceived')}
          buttonProps={{
            isDisabled: loading || receivedLoading,
            variant: 'texas-solid',
          }}
          onVerified={performReceived}
        />
      </ModalFooter>
    </>
  );
}
